import React, { useState, useEffect } from 'react';
import { DigitalTextbookModuleProps, TextbookProgress, ChapterProgress } from '../types';
import { Chapter } from '../types/content';
import { chaptersIndex } from '../data/chaptersIndex';
import { loadChapter } from '../lib/loaders';
import TopicChapterList from './TopicChapterList';
import StudyPathMap from './StudyPathMap';
import ChapterReader from './ChapterReader';
import MultiPageChapterReader from './MultiPageChapterReader';
import ChapterErrorBoundary from './ChapterErrorBoundary';
import { ProGate } from './ProGate';
import { getChapterAccess } from '../catalog/chapters';
import { useAuth } from '../AuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import {
  getAllChapterProgress,
  saveChapterProgress
} from '../utils/firebaseStorage';
import { autoCompleteTaskFromContent } from '../services/missionTaskCompletion';
import { MissionBanner } from './common/MissionBanner';
import { markTaskCompleted } from '../services/missionTaskCompletion';
import { Map, List } from 'lucide-react';

const DigitalTextbookModule: React.FC<DigitalTextbookModuleProps> = ({ onNavigate, resetToList, initialChapterId }) => {
  const { currentUser } = useAuth();
  const { hasPlus } = useEntitlements();
  // State management for overall module
  const [currentView, setCurrentView] = useState<'list' | 'reader'>('list');
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [chapterProgress, setChapterProgress] = useState<TextbookProgress>({});
  const [isLoadingChapter, setIsLoadingChapter] = useState(false);
  const [chapterLoadError, setChapterLoadError] = useState<string | null>(null);
  const [readerMode, setReaderMode] = useState<'full' | 'quiz-only'>('full');

  // View toggle state (map vs list)
  const [useMapView, setUseMapView] = useState(() => {
    // Check localStorage for preference, default to true (map view)
    const saved = localStorage.getItem('textbookViewMode');
    return saved ? saved === 'map' : true;
  });

  // Save view preference
  useEffect(() => {
    localStorage.setItem('textbookViewMode', useMapView ? 'map' : 'list');
  }, [useMapView]);

  // Mission Banner State
  const [showMissionBanner, setShowMissionBanner] = useState(false);
  const [missionBannerData, setMissionBannerData] = useState<any>(null);

  // Initialize component and load progress from localStorage
  useEffect(() => {
    const loadProgress = async () => {
      try {
        // Load all chapter progress from Firebase
        const loadedProgress = await getAllChapterProgress();
        setChapterProgress(loadedProgress);
      } catch (error) {
        console.error('Failed to initialize textbook progress:', error);
        // Continue with empty progress if Firebase fails
        setChapterProgress({});
      }
    };

    loadProgress();

    // Check for pending mission
    const pendingMission = localStorage.getItem('pendingMission');
    if (pendingMission) {
      try {
        const missionData = JSON.parse(pendingMission);
        const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');

        // Show if valid, not dismissed, and is ZSV module (or generic if we want)
        if (missionData &&
          missionData.createdAt &&
          (Date.now() - missionData.createdAt < 24 * 60 * 60 * 1000) &&
          !dismissed[missionData.missionKey || ''] &&
          (missionData.module === 'zsv' || !missionData.module) // Backwards compat
        ) {
          setMissionBannerData(missionData);
          setShowMissionBanner(true);
        }
      } catch (e) {
        console.error("Failed to parse pendingMission:", e);
      }
    }

    // Deep Linking: Check prop or URL params for chapter and mode
    const urlParams = new URLSearchParams(window.location.search);
    const targetChapter = initialChapterId || urlParams.get('chapter');
    const mode = urlParams.get('mode') as 'full' | 'quiz-only' | null;

    if (mode === 'quiz-only') {
      setReaderMode('quiz-only');
    }

    if (targetChapter) {
      handleChapterSelect(targetChapter);
    }
  }, [initialChapterId]);

  // Reset to list view when resetToList prop changes
  useEffect(() => {
    if (resetToList) {
      setCurrentView('list');
      setSelectedChapter(null);
      setIsLoadingChapter(false);
      setChapterLoadError(null);
    }
  }, [resetToList]);

  // Handle chapter selection and navigation to reader view
  const handleChapterSelect = async (chapterId: string) => {
    setIsLoadingChapter(true);
    setChapterLoadError(null);

    try {
      const chapter = await loadChapter(chapterId);
      setSelectedChapter(chapter);
      setCurrentView('reader');
    } catch (error) {
      console.error('Failed to load chapter:', error);
      setChapterLoadError(error instanceof Error ? error.message : 'Failed to load chapter');
    } finally {
      setIsLoadingChapter(false);
    }
  };

  // Handle navigation back to chapter list
  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedChapter(null);
    setChapterLoadError(null);
  };

  // Handle progress updates from ChapterReader
  const handleProgressUpdate = (updatedProgress: ChapterProgress) => {
    // Update local state
    setChapterProgress(prev => ({
      ...prev,
      [updatedProgress.chapterId]: updatedProgress
    }));

    // Persist to Firebase
    try {
      saveChapterProgress(updatedProgress.chapterId, {
        isCompleted: updatedProgress.isCompleted,
        quizScore: updatedProgress.quizScore,
        completedAt: updatedProgress.completedAt,
        readingProgress: updatedProgress.readingProgress
      }).catch(error => console.error('Failed to save chapter progress:', error));
    } catch (error) {
      console.error('Failed to save chapter progress:', error);
      // Local state is already updated, so UI will still work
    }

    // Auto-complete mission task when chapter is completed
    if (updatedProgress.isCompleted && currentUser) {
      autoCompleteTaskFromContent(
        currentUser.uid,
        'chapter',
        updatedProgress.chapterId
      ).then(() => {
        window.dispatchEvent(new Event('taskCompleted'));
      }).catch(error => console.error('Failed to auto-complete mission task:', error));
    }
  };

  // Render loading state
  if (isLoadingChapter) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Načítám kapitolu...</p>
        </div>
      </div>
    );
  }

  // Handle retry functionality
  const handleRetryChapterLoad = async () => {
    if (selectedChapter) {
      await handleChapterSelect(selectedChapter.id);
    }
  };

  // Render error state
  if (chapterLoadError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Chyba při načítání kapitoly
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {chapterLoadError}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRetryChapterLoad}
              className="bg-brand-orange text-white px-4 py-2 rounded-lg hover:bg-brand-orange/90 focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 transition-colors duration-200 font-poppins font-medium"
            >
              Zkusit znovu
            </button>
            <button
              onClick={handleBackToList}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Zpět na seznam
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render chapter reader view
  if (currentView === 'reader' && selectedChapter) {
    const currentProgress = chapterProgress[selectedChapter.id] || {
      chapterId: selectedChapter.id,
      isCompleted: false,
      readingProgress: 0
    };

    return (
      <div className="h-full">
        <ChapterErrorBoundary
          onError={(error) => {
            console.error('Chapter reader error:', error);
            setChapterLoadError('Chyba při zobrazování kapitoly');
          }}
        >
          <ProGate access={getChapterAccess(selectedChapter.id)} source="chapter">
            {selectedChapter.isMultiPage ? (
              <MultiPageChapterReader
                chapter={selectedChapter}
                progress={currentProgress}
                onProgressUpdate={handleProgressUpdate}
                onBackToList={handleBackToList}
              />
            ) : (
              <ChapterReader
                chapter={selectedChapter}
                progress={currentProgress}
                onProgressUpdate={handleProgressUpdate}
                onBackToList={handleBackToList}
                mode={readerMode}
              />
            )}
          </ProGate>
        </ChapterErrorBoundary>
      </div>
    );
  }

  // Render chapter list view (default)
  return (
    <main role="main" aria-label="Digitální učebnice" className="w-full overflow-x-hidden">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Digitální učebnice
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
              Studujte strukturovaný obsah a procvičujte si znalosti pomocí kvízů
            </p>
          </div>

          {/* View Toggle Button */}
          <button
            onClick={() => setUseMapView(!useMapView)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title={useMapView ? "Přepnout na klasický seznam" : "Přepnout na mapu studia"}
          >
            {useMapView ? (
              <>
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Klasický pohled</span>
              </>
            ) : (
              <>
                <Map className="w-4 h-4" />
                <span className="hidden sm:inline">Mapa studia</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* Mission Banner */}
      <MissionBanner
        visible={showMissionBanner && !!missionBannerData}
        label={missionBannerData?.label || "Dnešní mise"}
        focus={missionBannerData?.focus || "zsv"}
        minutes={missionBannerData?.minutes || 15}
        module="zsv"
        onStart={() => {
          // For ZSV, "Start" just validates we are here. 
          // Maybe scroll to relevant chapter if we can match 'focus' to chapter ID?
          // For now, just keep banner open or maybe close it?
          // User requirement: "Start sets activeMissionKey... and highlights relevant content"
          // Let's just close banner for MVP or keep it as a reminder?
          // "Banner must have 'Teď ne' (or X)... If user enters tutor/content manually... no banner"
          // Actually, if they entered via Plan, banner shows.
          // Let's just hide it on Start, assuming they are now "doing" it.
          // Or keep it as a "Current Mission" pill?
          // Re-reading: "Clicking ZSV mission must navigate... ZSV screen should show the same banner... Start sets activeMissionKey"
          // Let's just set activeMissionKey in LS and close banner maybe?
          // Or keep it.
          setShowMissionBanner(false);
        }}
        onDismiss={() => {
          if (missionBannerData?.missionKey) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedMissionKeys') || '{}');
            dismissed[missionBannerData.missionKey] = true;
            localStorage.setItem('dismissedMissionKeys', JSON.stringify(dismissed));
          }
          setShowMissionBanner(false);
        }}
        onComplete={() => {
          if (missionBannerData?.taskId) {
            markTaskCompleted(missionBannerData.taskId);
          }
          setShowMissionBanner(false);
        }}
        className="mb-6"
      />

      {useMapView ? (
        <StudyPathMap
          chapters={chaptersIndex.map((ch, i) => ({
            id: ch.id,
            title: ch.title,
            topic: ch.topic,
            completed: chapterProgress[ch.id]?.isCompleted || false,
            current: !chapterProgress[ch.id]?.isCompleted && (i === 0 || chapterProgress[chaptersIndex[i - 1].id]?.isCompleted),
            progress: chapterProgress[ch.id]?.readingProgress
          }))}
          onChapterClick={handleChapterSelect}
          hasPlus={hasPlus}
        /* 
        // Props temporarily disabled as new StudyPathMap doesn't support them yet
        onTopicSelect={(topicId: string) => {
          // Navigate to first chapter of the topic or show topic details
          const topicChapters = chaptersIndex.filter(ch => ch.topic === topicId);
          if (topicChapters.length > 0) {
            handleChapterSelect(topicChapters[0].id);
          }
        }}
        onFlashcardSelect={(setId: string) => {
          // Navigate to flashcards module
          if (onNavigate) {
            onNavigate(`/flashcards?set=${setId}`);
          }
        }}
        onUpgradePro={() => {
          // Navigate to upgrade page
          if (onNavigate) {
            onNavigate('/upgrade');
          }
        }}
        */
        />
      ) : (
        <TopicChapterList
          progress={chapterProgress}
          onChapterSelect={handleChapterSelect}
          onNavigate={onNavigate}
        />
      )}

      {/* Progress overview */}
      <section
        className="mt-8 p-4 sm:p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        aria-labelledby="progress-heading"
      >
        <div className="flex items-start gap-3">
          <div className="text-blue-600 dark:text-blue-400 mt-0.5" aria-hidden="true">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 id="progress-heading" className="font-medium text-blue-900 dark:text-blue-100 mb-1">Váš pokrok</h3>
            <p className="text-sm sm:text-base text-blue-800 dark:text-blue-200 mb-2">
              Dokončené kapitoly: {Object.values(chapterProgress).filter(p => p.isCompleted).length} z {chaptersIndex.length}
            </p>
            {Object.values(chapterProgress).filter(p => p.isCompleted).length > 0 && (
              <p className="text-sm sm:text-base text-blue-800 dark:text-blue-200">
                Průměrné skóre kvízů: {Math.round(
                  Object.values(chapterProgress)
                    .filter(p => p.isCompleted && p.quizScore !== undefined)
                    .reduce((sum, p) => sum + (p.quizScore || 0), 0) /
                  Object.values(chapterProgress).filter(p => p.isCompleted && p.quizScore !== undefined).length
                )}%
              </p>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default DigitalTextbookModule;